import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../hooks/useAuth';

const VideoIntroductionSection = ({ profile = {} }) => {
  const { user, userProfile } = useAuth();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [cspError, setCspError] = useState(false);

  // Handle fullscreen close
  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
    setVideoError(false);
    setCspError(false);
  };

  // Detect CSP errors
  useEffect(() => {
    const handleSecurityPolicyViolation = (e) => {
      if (e.blockedURI && e.blockedURI.includes('drive.google.com')) {
        console.error('[VideoIntroduction] CSP blocked Google Drive:', e);
        setCspError(true);
        setVideoError(true);
      }
    };

    document.addEventListener('securitypolicyviolation', handleSecurityPolicyViolation);
    return () => document.removeEventListener('securitypolicyviolation', handleSecurityPolicyViolation);
  }, []);

  // Helper: Extract Google Drive file ID from URL
  const getGoogleDriveFileId = (url) => {
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null;
  };

  // Helper: Check if URL is a direct video link (not Google Drive)
  const isDirectVideoLink = (url) => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Derive values
  const googleDriveFileId = profile?.video_introduction_url ? getGoogleDriveFileId(profile.video_introduction_url) : null;
  const isDirectVideo = profile?.video_introduction_url ? isDirectVideoLink(profile.video_introduction_url) : false;
  const isGoogleDrive = !!googleDriveFileId && !isDirectVideo;

  // Handle ESC key to close fullscreen (must be before early return)
  useEffect(() => {
    if (!isFullscreen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleCloseFullscreen();
      }
    };

    document.addEventListener('keydown', handleEsc);
    
    // Prevent body scroll when fullscreen is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = originalOverflow;
    };
  }, [isFullscreen]);
  
  // Check if video URL exists
  if (!profile?.video_introduction_url) {
    return null;
  }

  // Determine if viewing user can see the video
  const canViewVideo = () => {
    // If no user is logged in, don't show (for privacy)
    if (!user) {
      return false;
    }

    // VA can always view video on their own profile
    if (userProfile?.role === 'va') {
      return true;
    }

    // For employers, check if they have a paid subscription
    if (userProfile?.role === 'employer') {
      const paidTiers = ['pro', 'premium', 'agency'];
      return paidTiers.includes(userProfile?.subscription_tier);
    }

    // Default deny for other cases
    return false;
  };

  const isVideoAccessible = canViewVideo();
  const userRole = userProfile?.role;
  const isLoggedIn = !!user;
  const isFreeTierEmployer = userRole === 'employer' && !['pro', 'premium', 'agency'].includes(userProfile?.subscription_tier);
  const shouldShowUpgradePrompt = !isVideoAccessible && (profile?.video_introduction_url);  // Show prompt if video exists but user can't view

  return (
    <>
      {/* Video Section */}
      <section className="content-container py-12" id="video-introduction">
        <div className="mx-auto w-full lg:max-w-4xl">
          
          {/* Upgrade Prompt for Non-Logged-In Users */}
          {!isLoggedIn && shouldShowUpgradePrompt && (
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-300 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Icon name="Play" size={24} className="text-primary-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-primary-900 mb-2">
                    Watch this VA's Video Introduction
                  </h3>
                  <p className="text-primary-800 mb-4">
                    See this VA's video introduction to learn about their communication style and personality. Sign in or create an account as an employer to view videos and access other premium features.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/login-register?tab=login"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <Icon name="LogIn" size={18} />
                      Sign In
                    </Link>
                    <Link
                      to="/login-register?tab=register"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-colors"
                    >
                      <Icon name="UserPlus" size={18} />
                      Sign Up as Employer
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Premium Feature Lock (for free tier employers) */}
          {isFreeTierEmployer && shouldShowUpgradePrompt && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Icon name="Lock" size={24} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Premium Feature
                  </h3>
                  <p className="text-blue-800 mb-4">
                    Video introductions are available exclusively to our paid plan subscribers. This helps VAs showcase their communication style and personality directly to qualified employers like you.
                  </p>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                  >
                    <Icon name="Zap" size={18} />
                    Upgrade to Paid Plan
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Show video only if accessible */}
          {isVideoAccessible && (
            <>
              {/* Video Container - Responsive with 16:9 aspect ratio */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
            <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%' }}>
              {isGoogleDrive ? (
                // Google Drive Preview
                <>
                  <iframe
                    src={`https://drive.google.com/file/d/${googleDriveFileId}/preview`}
                    className="absolute top-0 left-0 w-full h-full border-0"
                    title="Video Introduction"
                    allow="autoplay"
                    onError={() => setVideoError(true)}
                  />
                  {videoError && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center flex-col gap-3">
                      <Icon name="AlertCircle" size={48} className="text-red-500" />
                      <p className="text-white text-sm text-center px-4">
                        {cspError 
                          ? 'Video blocked by security policy. Please contact support if this issue persists.'
                          : 'Unable to load video. Please check the video link and try again.'}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                // Direct Video Link
                <>
                  <video
                    src={profile.video_introduction_url}
                    className="absolute top-0 left-0 w-full h-full object-contain"
                    controls
                    controlsList="nodownload"
                    title="Video Introduction"
                    onError={() => setVideoError(true)}
                  />
                  {videoError && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center flex-col gap-3">
                      <Icon name="AlertCircle" size={48} className="text-red-500" />
                      <p className="text-white text-sm text-center px-4">
                        {cspError 
                          ? 'Video blocked by security policy. Please contact support if this issue persists.'
                          : 'Unable to load video. Please check the video link and try again.'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Video Controls - Fullscreen and Info */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Play" size={18} className="text-primary-600" />
                <span className="text-sm text-text-secondary font-medium">
                  {isGoogleDrive ? 'Video Introduction' : 'Video Introduction'}
                </span>
              </div>
              
              <button
                onClick={() => setIsFullscreen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-white hover:bg-gray-100 border border-gray-300 text-text-primary text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                title="Open in fullscreen"
                aria-label="Open video in fullscreen mode"
              >
                <Icon name="Maximize2" size={16} />
                <span className="hidden sm:inline">Fullscreen</span>
              </button>
            </div>
          </div>

          {/* Video Info */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={18} className="text-blue-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-1">About This Video</h4>
                <p className="text-sm text-blue-800">
                  This is a personal video introduction from the VA. It provides insight into their communication style and personality.
                </p>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </section>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-100"
            onClick={handleCloseFullscreen}
            aria-hidden="true"
          />
          
          {/* Modal Container */}
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center focus:outline-none"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fullscreen-video-title"
          >
            {/* Video Title (hidden but available for screen readers) */}
            <h2 id="fullscreen-video-title" className="sr-only">
              Video Introduction Fullscreen
            </h2>

            {/* Close Button */}
            <button
              onClick={handleCloseFullscreen}
              className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-white rounded-lg p-1"
              title="Close fullscreen (ESC)"
              aria-label="Close fullscreen video"
            >
              <Icon name="X" size={32} />
            </button>

            {/* Video Container */}
            <div 
              className="relative w-full h-full max-w-7xl max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {isGoogleDrive ? (
                // Google Drive Preview - Fullscreen
                <iframe
                  src={`https://drive.google.com/file/d/${googleDriveFileId}/preview`}
                  className="w-full h-full border-0 rounded-lg"
                  title="Video Introduction Fullscreen"
                  allow="autoplay"
                />
              ) : (
                // Direct Video - Fullscreen
                <video
                  src={profile.video_introduction_url}
                  className="w-full h-full object-contain rounded-lg"
                  controls
                  autoPlay
                  controlsList="nodownload"
                  title="Video Introduction Fullscreen"
                />
              )}
            </div>

            {/* Keyboard hint */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-center text-sm opacity-75">
              Press <kbd className="px-2 py-1 bg-gray-700 rounded">ESC</kbd> to exit fullscreen
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default VideoIntroductionSection;
